import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  MapPin, 
  CreditCard, 
  ArrowLeft, 
  CheckCircle2, 
  Truck, 
  Clock,
  ShieldCheck,
  Printer
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const OrderDetailsScreen = () => {
  const { id } = useParams();
  const { getConfig } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/orders/${id}`, getConfig());
        setOrder(data);

        // Check for payment complete query param
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('payment_complete') === 'true' && !data.isPaid) {
          const verifyRes = await axios.put(`${API_URL}/orders/${id}/verify`, {}, getConfig());
          setOrder(verifyRes.data);
          // Remove query parameter from URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching order:', error);
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, getConfig]);

  const handlePayNow = async () => {
    if (!window.Cashfree) {
      alert('Cashfree SDK failed to load. Please refresh and try again.');
      return;
    }

    setPaying(true);
    try {
      const { data } = await axios.get(`${API_URL}/orders/${order._id}/pay-session`, getConfig());
      
      const cashfree = new window.Cashfree({
        mode: process.env.REACT_APP_CASHFREE_ENV === 'production' ? 'production' : 'sandbox',
      });

      let checkoutOptions = {
        paymentSessionId: data.paymentSessionId,
        redirectTarget: '_modal',
      };

      cashfree.checkout(checkoutOptions).then(async (result) => {
        if (result.error) {
          console.error('Payment error:', result.error);
          alert(result.error.message || 'Payment failed or cancelled.');
          setPaying(false);
        } else {
          try {
            const verifyRes = await axios.put(`${API_URL}/orders/${order._id}/verify`, {}, getConfig());
            setOrder(verifyRes.data);
            if (verifyRes.data.isPaid) {
              alert('Payment successful!');
            } else {
              alert('Payment is still pending verification.');
            }
          } catch (verifyErr) {
            console.error('Verification error:', verifyErr);
            alert('Failed to verify payment status.');
          } finally {
            setPaying(false);
          }
        }
      });
    } catch (err) {
      console.error('Pay now error:', err);
      alert(err.response?.data?.message || 'Failed to initiate payment.');
      setPaying(false);
    }
  };

  if (loading) return <div className="p-20 text-center font-bold text-primary-600">Loading order details...</div>;
  if (!order) return <div className="p-20 text-center font-bold text-red-500">Order not found</div>;

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl">
      <Link to="/orders" className="inline-flex items-center space-x-2 text-secondary-500 hover:text-primary-600 transition-colors mb-8 font-bold text-sm">
        <ArrowLeft className="h-4 w-4" />
        <span>Back to My Orders</span>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          {/* Order Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4">
            <div className="space-y-2">
              <h1 className="text-4xl font-black italic text-secondary-900 uppercase tracking-tighter">Order Summary</h1>
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-xs font-black text-secondary-400 uppercase tracking-widest">Order ID: #{order._id}</span>
                <div className="h-1 w-1 bg-secondary-200 rounded-full" />
                <span className="text-xs font-black text-secondary-400 uppercase tracking-widest">{new Date(order.createdAt).toLocaleString()}</span>
              </div>
            </div>
            <Link
              to={`/orders/${order._id}/invoice`}
              className="inline-flex items-center space-x-2 bg-white border border-secondary-200 hover:bg-secondary-50 text-secondary-700 px-5 py-2.5 rounded-2xl font-bold transition-all text-sm shadow-sm"
            >
              <Printer className="h-4 w-4" />
              <span>Print Invoice</span>
            </Link>
          </div>

          {/* Items */}
          <div className="bg-white rounded-[40px] border border-secondary-100 p-8 shadow-sm space-y-6">
            <h2 className="text-xl font-black italic text-secondary-900 uppercase">Items Ordered</h2>
            <div className="divide-y divide-secondary-50">
              {order.orderItems.map((item, i) => (
                <div key={i} className="py-6 flex items-center space-x-6">
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-2xl object-cover shadow-sm" />
                  <div className="flex-1">
                    <h3 className="font-bold text-secondary-900">{item.name}</h3>
                    <p className="text-sm text-secondary-500 font-medium">Quantity: {item.qty}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-secondary-900 text-lg">₹{item.price * item.qty}</p>
                    <p className="text-xs text-secondary-400 font-medium">₹{item.price} each</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery & Payment Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-[32px] border border-secondary-100 p-8 shadow-sm space-y-4">
              <div className="flex items-center space-x-3 text-primary-600">
                <MapPin className="h-5 w-5" />
                <h3 className="font-black italic uppercase text-sm">Shipping To</h3>
              </div>
              <div className="space-y-1 text-secondary-600 font-medium">
                <p className="font-bold text-secondary-900">{order.user?.name}</p>
                <p>{order.shippingAddress.address}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.postalCode}</p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>

            <div className="bg-white rounded-[32px] border border-secondary-100 p-8 shadow-sm space-y-4">
              <div className="flex items-center space-x-3 text-primary-600">
                <CreditCard className="h-5 w-5" />
                <h3 className="font-black italic uppercase text-sm">Payment Info</h3>
              </div>
              <div className="space-y-3">
                <p className="font-bold text-secondary-900">{order.paymentMethod}</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`inline-flex items-center space-x-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    order.isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {order.isPaid ? <><ShieldCheck className="h-3 w-3" /><span>Paid {new Date(order.paidAt).toLocaleDateString()}</span></> : 'Payment Pending'}
                  </div>
                  {!order.isPaid && (
                    <button
                      onClick={handlePayNow}
                      disabled={paying}
                      className="bg-primary-600 hover:bg-primary-700 text-white text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full transition-all disabled:opacity-50"
                    >
                      {paying ? 'Paying...' : 'Pay Now'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Status */}
        <div className="space-y-8">
          <div className="bg-secondary-900 rounded-[40px] p-8 text-white space-y-8 sticky top-28">
            <div className="space-y-2">
              <p className="text-[10px] font-black text-secondary-400 uppercase tracking-widest">Order Status</p>
              <div className="flex items-center space-x-3">
                {order.isDelivered ? <CheckCircle2 className="h-8 w-8 text-emerald-400" /> : <Clock className="h-8 w-8 text-amber-400" />}
                <h2 className="text-2xl font-black italic uppercase">{order.isDelivered ? 'Delivered' : 'Processing'}</h2>
              </div>
            </div>

            <div className="space-y-4">
               <div className="flex justify-between text-secondary-400 font-bold uppercase text-[10px] tracking-widest">
                 <span>Items Subtotal</span>
                 <span className="text-white">₹{order.itemsPrice}</span>
               </div>
               {order.discountPrice > 0 && (
                 <div className="flex justify-between text-red-400 font-bold uppercase text-[10px] tracking-widest">
                   <span>Discount ({order.promoCode})</span>
                   <span className="text-red-400">-₹{order.discountPrice}</span>
                 </div>
               )}
               <div className="flex justify-between text-secondary-400 font-bold uppercase text-[10px] tracking-widest">
                 <span>Shipping</span>
                 <span className="text-emerald-400">FREE</span>
               </div>
               <div className="h-px bg-secondary-800" />
               <div className="flex justify-between items-end">
                 <span className="text-sm font-black uppercase text-secondary-400 tracking-widest">Total Amount</span>
                 <span className="text-3xl font-black text-primary-400 tracking-tighter">₹{order.totalPrice}</span>
               </div>
            </div>

            {!order.isDelivered && (
              <div className="bg-secondary-800 rounded-3xl p-5 flex items-start space-x-4">
                <Truck className="h-6 w-6 text-primary-400 mt-1" />
                <div className="space-y-1">
                  <p className="font-bold text-sm">On its way!</p>
                  <p className="text-xs text-secondary-400 leading-relaxed">Your order is being prepared and will be shipped shortly.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsScreen;
